import { useState, useEffect, useRef } from 'react';
import { CurrencyCode } from '../constants/currencies';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';
import { Landmark } from 'lucide-react';
import { useParsedCurrencyRates } from '../hooks/useParsedCurrencyRates';
import { useGroupMemberAccountInfo } from '../hooks/queries/useGroupMemberAccountInfo';
import { isMobile } from 'react-device-detect';
import kakaoImage from '../assets/kakao.webp';
import tossImage from '../assets/toss.webp';

interface TransferModalProps {
  toId: string;
  toName: string | undefined;
  amount: number;
  currency: CurrencyCode;
  openConfirmModal: (channel: string) => void;
}

const TransferModal: React.FC<TransferModalProps> = ({
  toId,
  toName, 
  amount, 
  currency,
  openConfirmModal,
}) => {
  const [isShowCopiedMessage, setIsShowCopiedMessage] = useState(false);  
  const {currencyMap, isLoading, latestUpdatedAt} = useParsedCurrencyRates();

  const {data: memberAccountInfo, isLoading: isMemberAccountInfoLoading} = useGroupMemberAccountInfo(toId);


  const KRWAmount = (currencyMap.get(currency) || 0) * amount;
  const KRWAmountString = formatCurrency(KRWAmount, "KRW").replace('₩','')+'원';
  const lastUpdatedAtString = new Date(latestUpdatedAt).toLocaleDateString('ko-KR', {month: '2-digit', day: '2-digit'}).replace(/\. /g, '/').replace('.', '');

  const hasAppSwitched = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hasAppSwitched.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [openConfirmModal]);

  const onTossClick = () => {
    hasAppSwitched.current = false;
    
    setTimeout(() => {
      if (!hasAppSwitched.current) {
        toast.error('토스 앱을 열 수 없습니다');
      } else {
        openConfirmModal('토스')
      }
    }, 3000);
    
    window.location.href = `supertoss://send?amount=${KRWAmount}&bank=${encodeURIComponent(memberAccountInfo?.bank || '')}&accountNo=${memberAccountInfo?.account_number}`;
  }

  const onKakaoClick = () => {
    const newWindow = window.open(`https://qr.kakaopay.com/${memberAccountInfo?.kakaopay_id}${((KRWAmount * 524288).toString(16))}`, '_blank');
    setTimeout(()=>{
      newWindow.close();
      openConfirmModal('카카오')
    },3000)
  }


  const onCopyClick = () => {
    const bank = memberAccountInfo?.bank;
    const accountNumber = memberAccountInfo?.account_number;
    if (!bank || !accountNumber) {
      toast.error('계좌번호를 찾을 수 없습니다');
      return;
    }

    navigator.clipboard.writeText(`${bank} ${accountNumber}`)
      .then(() => {
        toast(`${bank} ${accountNumber}`, {
          icon: '🏦',
          duration: 2000,
        });
        toast.success('계좌번호가 클립보드에 복사되었습니다!');
        setIsShowCopiedMessage(true);
        setTimeout(() => {
          setIsShowCopiedMessage(false);
        }, 2000);
      })
      .catch(err => {
        console.error('클립보드 복사 실패:', err);
        toast.error('계좌번호 복사에 실패했습니다');
      });
  };

  const cannotTransfer = !memberAccountInfo?.kakaopay_id && !memberAccountInfo?.account_number;

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-4 text-gray-800">송금하기</h2>
      <p className="text-xl font-bold text-gray-800 mb-2">
        {toName}에게 {formatCurrency(amount, currency)} 보내기
      </p>
      <p className="text-gray-500 mb-6">
        { (!isMemberAccountInfoLoading && cannotTransfer) ? '상대방이 수령계좌를 등록하지 않았어요.' : '송금 완료후, 정산완료 버튼을 눌러주세요!'}
        {
          currency !== 'KRW' && (
            <span>
              <br/>
            {
              isLoading ? '송금 환율 계산중...' : `(${KRWAmountString}, ${lastUpdatedAtString}기준)`
            }
          </span>
          )
        }
      </p>
      
      {
        isMemberAccountInfoLoading ? (
          <div className="w-full flex flex-col justify-center items-center gap-2 my-18">
            <div className="w-6 h-6 border-3 border-gray-800 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : (
        <div className="w-full flex flex-col justify-center items-center gap-2">
          <button 
            className="w-full py-2.5 font-bold text-lg bg-[#FEE500] text-[#333333] rounded-xl flex items-center justify-center gap-2 pr-4"
            onClick={onKakaoClick}
            disabled={isMemberAccountInfoLoading || !memberAccountInfo?.kakaopay_id || !isMobile}
          >
            <img src={kakaoImage} alt="kakao" className="w-4 h-4 inline-block" />
            카카오 송금
          </button>
          <button 
            className="w-full py-2.5 font-bold text-lg bg-[#202632] text-white rounded-xl flex items-center justify-center gap-2 pr-4"
            onClick={onTossClick}
            disabled={isMemberAccountInfoLoading || !memberAccountInfo?.account_number || !isMobile}
          >
            <img src={tossImage} alt="toss" className="w-4 h-4 inline-block" />
            토스 송금
          </button>
          <button 
            className="w-full py-2.5 font-bold text-lg bg-gray-200 text-gray-800 rounded-xl flex items-center justify-center gap-2 pr-4"
            onClick={onCopyClick}
            disabled={isMemberAccountInfoLoading || !memberAccountInfo?.account_number}
          >
            <Landmark className="w-4 h-4 inline-block" />
            {
              isShowCopiedMessage ? '계좌번호 복사 완료' : '직접 송금'
            }
          </button>
          <p className="text-gray-800 underline mt-1 cursor-pointer" onClick={()=>openConfirmModal(null)}>송금이 벌써 완료됐나요?</p>
        </div>
      )}
    </div>
  );
};

export default TransferModal;